import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Component, useEffect } from 'react';
import MainLayout from './Layout/MainLayout';
import ProgressBar from "./Global/ProgressBar";
import Login from './Pages/Login/Login';
import { AuthProvider } from './context/AuthContext';
import { CacheProvider } from './context/CacheContext';
import ProtectedRoute from './routes/ProtectedRoute';
import { warmUpCache } from './api/offlineFetcher';

// Dashboard Pages
import ExecutiveOverview from "./Pages/Dashboard/ExecutiveOverview";
import GeospatialOverview from "./Pages/Dashboard/GeospatialOverview";

// Core Modules Pages
import LivestockPopulation from "./Pages/CoreModules/LivestockPopulation";
import LivestockAddRecord from "./Pages/ModuleRecords/LivestockAddRecord";
import MarketsTrade from "./Pages/CoreModules/MarketsTrade";
import MarketAddRecord from "./Pages/ModuleRecords/MarketAddRecord";
import Infrastructure from "./Pages/CoreModules/Infrastructure";
import InfrastructureAddRecord from "./Pages/ModuleRecords/InfrastructureAddRecord";
import WorkforceInstitutional from "./Pages/CoreModules/WorkforceInstitutional";
import WorkforceInstitutionalAddRecord from "./Pages/ModuleRecords/WorkforceInstitutionalAddRecord";
import PlanningReporting from "./Pages/CoreModules/PlanningReporting";
import PlanningReportingAddRecord from "./Pages/ModuleRecords/PlanningReportingAddRecord";
import ConflictsDispute from "./Pages/CoreModules/ConflictsDispute";
import ConflictsDisputeAddRecord from "./Pages/ModuleRecords/ConflictsDisputeAddRecord";

// Cross-cutting registries (sit above the eight statistical modules)
import PppArrangements from "./Pages/CoreModules/PppArrangements";
import PppArrangementAddRecord from "./Pages/ModuleRecords/PppArrangementAddRecord";

// Integrated Modules Pages
import AnimalIdentification from "./Pages/IntegratedModules/AnimalIdentification";
import AnimalHealth from "./Pages/IntegratedModules/AnimalHealth";

// Management Pages
import TrustCenter, { RegistryManagement } from "./Pages/Management/Admin";

// Module route helper — resolves a module's index redirect from the live
// module/submodule catalog instead of a hardcoded first-tab key.
import ModuleIndexRedirect from "./component/shared/ModuleIndexRedirect";

// Public app
import PublicApp from './Pages/public/App';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', fontFamily: 'sans-serif', color: '#dc2626' }}>
          <h2>Something went wrong on this page.</h2>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', color: '#374151' }}>
            {this.state.error?.message}
          </pre>
          <button
            onClick={() => window.location.assign('/executive-overview')}
            style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}
          >
            Return to Dashboard
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  useEffect(() => {
    void warmUpCache();
  }, []);

  return (
    <CacheProvider>
    <AuthProvider>
    <Router>
      <ProgressBar />
      <Routes>
        {/* 🔓 Public Routes */}
        <Route path="/" element={<Navigate to="/public" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/public/*" element={<PublicApp />} />

        {/* 🔐 Protected Routes - Wrapped with MainLayout */}
        <Route element={<ErrorBoundary><ProtectedRoute /></ErrorBoundary>}>
        <Route element={<MainLayout />}>
          <Route path="dashboard" element={<ExecutiveOverview />} />
          <Route path="executive-overview" element={<ExecutiveOverview />} />
          <Route path="geospatial-overview" element={<GeospatialOverview />} />

          {/* ── Core Modules ── */}

          {/* Livestock Population — nested tab routes. `:tab` matches any
              submodule key from the module catalog, not just the ones
              built in below, so a submodule added via the Form Registry
              resolves to this page instead of 404ing. */}
          <Route path="livestock-population">
            <Route index element={<ModuleIndexRedirect moduleId="livestock-population" fallback="livestock-population-record" />} />
            <Route path="new"        element={<LivestockAddRecord />} />
            <Route path="edit/:uuid" element={<LivestockAddRecord />} />
            <Route path="view/:uuid" element={<LivestockAddRecord viewOnly />} />
            <Route path=":tab"       element={<LivestockPopulation />} />
          </Route>

          <Route path="markets-trade">
            <Route index element={<ModuleIndexRedirect moduleId="markets-trade" fallback="market-trade" />} />
            <Route path="new"        element={<MarketAddRecord />} />
            <Route path="edit/:uuid" element={<MarketAddRecord />} />
            <Route path="view/:uuid" element={<MarketAddRecord viewOnly />} />
            <Route path=":tab"       element={<MarketsTrade />} />
          </Route>

          <Route path="infrastructure">
            <Route index element={<ModuleIndexRedirect moduleId="infrastructure" fallback="core" />} />
            <Route path="new"        element={<InfrastructureAddRecord />} />
            <Route path="edit/:uuid" element={<InfrastructureAddRecord />} />
            <Route path="view/:uuid" element={<InfrastructureAddRecord viewOnly />} />
            <Route path=":tab"       element={<Infrastructure />} />
          </Route>

          <Route path="workforce-institutional">
            <Route index element={<ModuleIndexRedirect moduleId="workforce-institutional" fallback="actors" />} />
            <Route path="new"        element={<WorkforceInstitutionalAddRecord />} />
            <Route path="edit/:uuid" element={<WorkforceInstitutionalAddRecord />} />
            <Route path="view/:uuid" element={<WorkforceInstitutionalAddRecord viewOnly />} />
            <Route path=":tab"       element={<WorkforceInstitutional />} />
          </Route>

          <Route path="planning-reporting">
            <Route index element={<ModuleIndexRedirect moduleId="planning-reporting" fallback="workplans" />} />
            <Route path="new"        element={<PlanningReportingAddRecord />} />
            <Route path="edit/:uuid" element={<PlanningReportingAddRecord />} />
            <Route path="view/:uuid" element={<PlanningReportingAddRecord viewOnly />} />
            <Route path=":tab"       element={<PlanningReporting />} />
          </Route>

          <Route path="conflicts-dispute">
            <Route index element={<ModuleIndexRedirect moduleId="conflicts-dispute" fallback="records" />} />
            <Route path="new"        element={<ConflictsDisputeAddRecord />} />
            <Route path="edit/:uuid" element={<ConflictsDisputeAddRecord />} />
            <Route path="view/:uuid" element={<ConflictsDisputeAddRecord viewOnly />} />
            <Route path=":tab"       element={<ConflictsDispute />} />
          </Route>

          {/* Cross-cutting registries — sit above the eight statistical modules */}
          <Route path="ppp-arrangements">
            <Route index element={<Navigate to="arrangements" replace />} />
            <Route path="arrangements" element={<PppArrangements />} />
            <Route path="new"          element={<PppArrangementAddRecord />} />
            <Route path="edit/:uuid"   element={<PppArrangementAddRecord />} />
            <Route path="view/:uuid"   element={<PppArrangementAddRecord viewOnly />} />
          </Route>

          {/* Integrated Modules */}
          <Route path="animal-identification">
            <Route index element={<ModuleIndexRedirect moduleId="animal-identification" fallback="registrations" />} />
            <Route path=":tab" element={<AnimalIdentification />} />
          </Route>

          <Route path="animal-health">
            <Route index element={<ModuleIndexRedirect moduleId="animal-health" fallback="outbreak" />} />
            <Route path=":tab" element={<AnimalHealth />} />
          </Route>

          {/* Management */}
          <Route path="admin">
            <Route index element={<Navigate to="institutions" replace />} />
            <Route path="institutions"  element={<TrustCenter />} />
            <Route path="users">
              <Route index element={<Navigate to="list" replace />} />
              <Route path="list"        element={<TrustCenter />} />
              <Route path="assignments" element={<TrustCenter />} />
            </Route>
            <Route path="geospatial"    element={<TrustCenter />} />
            <Route path="audit"         element={<TrustCenter />} />
            <Route path="supplementary" element={<TrustCenter />} />
            <Route path="baseline"      element={<TrustCenter />} />
            {/* Registries moved to Registry Management — old links still land */}
            <Route path="species/*"     element={<Navigate to="/registry-management/species" replace />} />
            <Route path="departments"   element={<Navigate to="/registry-management/departments" replace />} />
            <Route path="forms"         element={<Navigate to="/registry-management/forms" replace />} />
            <Route path="ppp"           element={<Navigate to="/registry-management/ppp" replace />} />
          </Route>
          <Route path="registry-management">
            <Route index element={<Navigate to="species" replace />} />
            <Route path="species">
              <Route index element={<Navigate to="species-registry" replace />} />
              <Route path="species-registry" element={<RegistryManagement />} />
              <Route path="breed"            element={<RegistryManagement />} />
              <Route path="purpose"          element={<RegistryManagement />} />
              <Route path="age"              element={<RegistryManagement />} />
            </Route>
            <Route path="departments"   element={<RegistryManagement />} />
            <Route path="forms"         element={<RegistryManagement />} />
            <Route path="ppp"           element={<RegistryManagement />} />
            <Route path="central"       element={<RegistryManagement />} />
          </Route>
        </Route>
        </Route>
      </Routes>
    </Router>
    </AuthProvider>
    </CacheProvider>
  );
}

export default App;
